import type {MatchMode} from '@wandb/weave/common/util/columnMatching';
import {Icon} from '@wandb/weave/components/Icon';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React from 'react';

import type {Config} from '../../../util/runfeed';
import {COLUMN_LIMIT} from '../../../util/tablecols';
import {Banner} from '../../Banner';
import {TableEditorColumns} from './TableEditorColumns';
import {TableEditorSearch} from './TableEditorSearch';
import {useTableEditorSearchMatches} from './useTableEditorSearchMatches';

interface TableEditorProps {
  allColumns: string[];
  config: Config;
  fixedColumns: string[];
  isTableExpandable?: boolean;
  onSetMatchMode(mode: MatchMode): void;
  onSetSearchQuery(search: string): void;
  onUpdate(config: Config): void;
  searchQuery: string;
  searchMode: MatchMode;
}

export function TableEditor({
  allColumns,
  config,
  fixedColumns: fixedColumnsProp,
  isTableExpandable,
  onSetMatchMode,
  onSetSearchQuery,
  onUpdate,
  searchQuery,
  searchMode,
}: TableEditorProps) {
  const {
    fixedColumns,
    hiddenColumns,
    highlightColumn,
    pinnedColumns,
    visibleColumns,
  } = useTableEditorSearchMatches({
    allColumns,
    config,
    fixedColumns: fixedColumnsProp,
    searchMode,
    searchQuery,
  });

  const isColumnLimitReached = config.columnOrder.length >= COLUMN_LIMIT;
  return (
    <TailwindContents>
      <div className="flex flex-col gap-16">
        <p>
          Configure columns to display in the table.
          {isTableExpandable &&
            ' Pinned columns persist when the table is collapsed.'}
        </p>
        {isColumnLimitReached && (
          <Banner variant="warning">
            <div className="flex gap-8 font-semibold">
              <Icon name="warning" />
              You have reached the maximum number of visible columns allowed.
            </div>
          </Banner>
        )}
        <TableEditorSearch
          onSetMatchMode={onSetMatchMode}
          onSetSearch={onSetSearchQuery}
          search={searchQuery}
          matchMode={searchMode}
        />
        <TableEditorColumns
          config={config}
          fixedColumns={fixedColumns}
          hiddenColumns={hiddenColumns}
          highlightColumn={highlightColumn}
          isColumnLimitReached={isColumnLimitReached}
          onUpdate={onUpdate}
          pinnedColumns={pinnedColumns}
          visibleColumns={visibleColumns}
        />
      </div>
    </TailwindContents>
  );
}
